import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, ArrowsOut, Crop } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const r2 = (n) => Math.round(n * 100) / 100;

// Photo placement in sheet inches: top-left offset, inches-per-pixel scale, quarter turns.
const coverFit = (iw, ih, L, W) => {
  const ipx = Math.max(L / iw, W / ih);
  return { ipx, x: (L - iw * ipx) / 2, y: (W - ih * ipx) / 2, rot: 0 };
};

/** Line the remnant photo up under the traced usable field, then cut a transparent
 *  PNG of just that shape — the picture of the actual piece that goes on the slab layout. */
export const ShapeCropDialog = ({ photo, polygon = [], lengthIn, widthIn, code = "", onDone, onClose }) => {
  const L = Number(lengthIn) || 1;
  const W = Number(widthIn) || 1;
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const [img, setImg] = useState(null);
  const [url, setUrl] = useState("");
  const [fit, setFit] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    let objUrl = "";
    api
      .get(`/photos/${photo.storage_path}`, { responseType: "blob" })
      .then(({ data }) => {
        objUrl = URL.createObjectURL(data);
        const im = new Image();
        im.onload = () => {
          if (!alive) return;
          setImg(im);
          setUrl(objUrl);
          setFit(coverFit(im.naturalWidth, im.naturalHeight, L, W));
        };
        im.onerror = () => alive && toast.error("Could not open that photo");
        im.src = objUrl;
      })
      .catch(() => alive && toast.error("Could not open that photo"));
    return () => {
      alive = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [photo.storage_path, L, W]);

  const inches = useMemo(() => polygon.map((p) => [r2(p.x * L), r2(p.y * W)]), [polygon, L, W]);
  const M = Math.max(L, W) * 0.08;
  const fs = Math.max(L, W) * 0.028;
  const base = img ? coverFit(img.naturalWidth, img.naturalHeight, L, W).ipx : 1;

  const toSvg = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const loc = pt.matrixTransform(svg.getScreenCTM().inverse());
    return [loc.x, loc.y];
  };

  const onDown = (e) => {
    if (!fit) return;
    e.preventDefault();
    svgRef.current.setPointerCapture?.(e.pointerId);
    dragRef.current = { at: toSvg(e), x: fit.x, y: fit.y };
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const [x, y] = toSvg(e);
    setFit((f) => ({ ...f, x: d.x + x - d.at[0], y: d.y + y - d.at[1] }));
  };
  const onUp = () => {
    dragRef.current = null;
  };
  const onWheel = (e) => {
    if (!fit) return;
    const [mx, my] = toSvg(e);
    const k = e.deltaY < 0 ? 1.08 : 1 / 1.08;
    setFit((f) => zoomAbout(f, k, mx, my));
  };
  // zoom keeping the sheet point under the cursor fixed
  const zoomAbout = (f, k, mx, my) => ({
    ...f,
    ipx: f.ipx * k,
    x: mx - (mx - f.x) * k,
    y: my - (my - f.y) * k,
  });

  const w = img && fit ? img.naturalWidth * fit.ipx : 0;
  const h = img && fit ? img.naturalHeight * fit.ipx : 0;
  const photoTransform = fit ? `translate(${fit.x} ${fit.y}) rotate(${fit.rot} ${w / 2} ${h / 2})` : "";
  const ring = inches.map((p) => p.join(",")).join(" ");
  const maskPath =
    `M${-M} ${-M} h${L + 2 * M} v${W + 2 * M} h${-(L + 2 * M)} Z ` +
    (inches.length ? `M${inches.map((p) => p.join(" ")).join(" L")} Z` : "");

  const labels = inches.map((a, i) => {
    const b = inches[(i + 1) % inches.length];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const mx = (a[0] + b[0]) / 2;
    const my = (a[1] + b[1]) / 2;
    // push the label outward from the shape's centre
    const cx = inches.reduce((s, p) => s + p[0], 0) / inches.length;
    const cy = inches.reduce((s, p) => s + p[1], 0) / inches.length;
    const nx = -(b[1] - a[1]) / (len || 1);
    const ny = (b[0] - a[0]) / (len || 1);
    const out = (mx - cx) * nx + (my - cy) * ny >= 0 ? 1 : -1;
    return { i, len: Math.round(len * 10) / 10, x: mx + nx * out * fs * 1.6, y: my + ny * out * fs * 1.6 };
  });

  const crop = async () => {
    if (!img || !fit || inches.length < 3) return;
    setBusy(true);
    try {
      const pxPerIn = Math.min(16, 2400 / Math.max(L, W));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(L * pxPerIn));
      canvas.height = Math.max(1, Math.round(W * pxPerIn));
      const ctx = canvas.getContext("2d");
      ctx.scale(pxPerIn, pxPerIn);
      ctx.beginPath();
      inches.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.closePath();
      ctx.clip();
      ctx.translate(fit.x, fit.y);
      ctx.translate(w / 2, h / 2);
      ctx.rotate((fit.rot * Math.PI) / 180);
      ctx.translate(-w / 2, -h / 2);
      ctx.drawImage(img, 0, 0, w, h);
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
      if (!blob) throw new Error("no blob");
      await onDone(
        new File([blob], `${(code || "remnant").replace(/[^\w-]+/g, "_")}-shape.png`, { type: "image/png" }),
      );
    } catch {
      toast.error("Could not crop that photo");
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-h-[94vh] overflow-y-auto border-zinc-800 bg-zinc-950 sm:max-w-5xl"
        data-testid="shape-crop-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-100">
            <Crop size={18} weight="bold" className="text-indigo-300" /> Crop photo to the traced shape
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Drag the photo (scroll to zoom) until the real stone edges sit on the dashed outline —{" "}
            {L}" × {W}" sheet. Everything outside the shape is cut away and saved as a see-through PNG.
          </DialogDescription>
        </DialogHeader>

        {inches.length < 3 ? (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="shape-crop-no-polygon">
            Trace the usable field on this remnant first (Edit remnant → trace), then come back to crop.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2" data-testid="shape-crop-tools">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Zoom</span>
              <input
                type="range"
                data-testid="shape-crop-zoom"
                min="0.25"
                max="4"
                step="0.01"
                disabled={!fit}
                value={fit ? fit.ipx / base : 1}
                onChange={(e) =>
                  setFit((f) => zoomAbout(f, (Number(e.target.value) * base) / f.ipx, L / 2, W / 2))
                }
                className="h-1.5 w-40 accent-indigo-400"
              />
              <span className="font-mono-data w-12 text-[10px] text-zinc-400">
                {fit ? Math.round((fit.ipx / base) * 100) : 100}%
              </span>
              <Button
                type="button"
                variant="outline"
                data-testid="shape-crop-rotate"
                disabled={!fit}
                onClick={() => setFit((f) => ({ ...f, rot: (f.rot + 90) % 360 }))}
                className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
              >
                <ArrowClockwise size={14} weight="bold" className="mr-1" /> 90°
              </Button>
              <Button
                type="button"
                variant="outline"
                data-testid="shape-crop-reset"
                disabled={!img}
                onClick={() => setFit(coverFit(img.naturalWidth, img.naturalHeight, L, W))}
                className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
              >
                <ArrowsOut size={14} weight="bold" className="mr-1" /> Reset fit
              </Button>
              <Button
                type="button"
                data-testid="shape-crop-confirm"
                disabled={!fit || busy}
                onClick={crop}
                className="ml-auto h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                <Crop size={14} weight="bold" className="mr-1" /> {busy ? "Cropping…" : "Crop & save as photo"}
              </Button>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-[repeating-conic-gradient(#27272a_0_25%,#1c1c1f_0_50%)] bg-[length:22px_22px]">
              <svg
                ref={svgRef}
                data-testid="shape-crop-stage"
                viewBox={`${-M} ${-M} ${L + 2 * M} ${W + 2 * M}`}
                className={`w-full select-none ${fit ? "cursor-grab active:cursor-grabbing" : ""}`}
                style={{ touchAction: "none", maxHeight: "62vh" }}
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onUp}
                onWheel={onWheel}
              >
                {url && fit && (
                  <g transform={photoTransform}>
                    <image href={url} x={0} y={0} width={w} height={h} preserveAspectRatio="none" />
                  </g>
                )}
                {!img && (
                  <text x={L / 2} y={W / 2} textAnchor="middle" fill="#a1a1aa" fontSize={fs * 1.4}>
                    Loading photo…
                  </text>
                )}
                <path d={maskPath} fill="rgba(9,9,11,0.62)" fillRule="evenodd" className="pointer-events-none" />
                <rect x={0} y={0} width={L} height={W} fill="none" stroke="#52525b" strokeWidth={fs * 0.06} strokeDasharray={`${fs * 0.4} ${fs * 0.4}`} className="pointer-events-none" />
                <polygon points={ring} fill="none" stroke="#09090b" strokeWidth={fs * 0.22} className="pointer-events-none" />
                <polygon
                  points={ring}
                  fill="none"
                  stroke="#fafafa"
                  strokeWidth={fs * 0.12}
                  strokeDasharray={`${fs * 0.7} ${fs * 0.5}`}
                  className="pointer-events-none"
                  data-testid="shape-crop-outline"
                />
                {labels.map((lab) => (
                  <g key={lab.i} className="pointer-events-none">
                    <rect
                      x={lab.x - fs * 1.9}
                      y={lab.y - fs * 0.7}
                      width={fs * 3.8}
                      height={fs * 1.4}
                      rx={fs * 0.25}
                      fill="#fafafa"
                      stroke="#18181b"
                      strokeWidth={fs * 0.06}
                    />
                    <text
                      x={lab.x}
                      y={lab.y + fs * 0.38}
                      textAnchor="middle"
                      fontSize={fs}
                      fontWeight="700"
                      fill="#18181b"
                    >
                      {`${lab.len}"`}
                    </text>
                  </g>
                ))}
              </svg>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
